import { Container, Text } from "pixi.js";
import { WinAmountLabel } from "@slot/presentation";
import type { WinAmountLabelFactory } from "@slot/presentation";

/** The label's look: bold white Roboto on a chocolate outline (the badge digits' orange is for multipliers). */
const LABEL_STYLE = {
  fontFamily: "Roboto, system-ui, sans-serif",
  fontWeight: "700",
  fontSize: 80,
  fill: 0xffffff,
  stroke: { color: 0x5a2a12, width: 12, join: "round" },
  dropShadow: { color: 0x000000, alpha: 0.45, blur: 6, distance: 4, angle: Math.PI / 2 },
  align: "center",
} as const;

/** The entrance: from 40 % to full size, overshooting on `backOut`. */
const POP = { fromScale: 0.4, seconds: 0.28 } as const;

/**
 * The candy win label: a cluster's amount in Pixi text (the title has no amount font: its sprite font is the badges'
 * digits and "x" only), popping in where the win shows. A cluster its badges multiplied shows "base × multiplier" first,
 * then pops to its total (the tuning's `multiplier` beat), so the player reads what the badges did. Shares the base
 * label's exits — the fade, the collect.
 */
class CandyWinAmountLabel extends WinAmountLabel {
  public override show(text: string, breakdown?: { baseText: string; multiplier: string }): void {
    this.applyText(breakdown ? `${breakdown.baseText} × ${breakdown.multiplier}` : text);
    const node = this.node;
    node.alpha = 1;
    const runner = this.taskRunner;
    if (!runner) {
      this.applyText(text);
      node.scale.set(1, 1);
      return;
    }
    const generation = ++this.generation;
    const current = (): boolean => this.generation === generation && !node.destroyed;
    node.scale.set(POP.fromScale, POP.fromScale);
    void (async () => {
      await runner.tweenProgress(
        POP.seconds,
        (k) => {
          if (current()) node.scale.set(POP.fromScale + (1 - POP.fromScale) * k, POP.fromScale + (1 - POP.fromScale) * k);
        },
        undefined,
        { easing: "backOut" },
      );
      const beat = this.tuning?.multiplier;
      if (!breakdown || !beat || !current()) return;
      await runner.sleep(beat.holdSeconds);
      // The pop-replace: a swell, the total at its peak, back to rest.
      let swapped = false;
      await runner.tweenProgress(
        beat.popSeconds,
        (k) => {
          if (!current()) return;
          if (!swapped && k >= 0.5) {
            swapped = true;
            this.applyText(text);
          }
          const scale = 1 + (beat.popScale - 1) * Math.sin(Math.PI * k);
          node.scale.set(scale, scale);
        },
        undefined,
        { easing: "linear" },
      );
    })();
  }
}

/** Builds one label per win; `WinHighlightEffect` calls it and destroys the result. */
export function createWinAmountLabelFactory(): WinAmountLabelFactory {
  return () => {
    const root = new Container({ label: "WinAmountLabel" });
    const label = new Text({ text: "", style: LABEL_STYLE, label: "Amount" });
    label.anchor.set(0.5);
    root.addChild(label);
    return new CandyWinAmountLabel({ node: root, label });
  };
}
